import {BackendFetch, Models} from '@hantera/portal-app';
import {Cache} from '@/backend/cache';
import {BackendGraph} from "./graph";

const saving: {
  [orderId: string]: boolean
} = {};

const cache = new Cache<Models.Graph.Order>(o => o.orderId);



export default function (portalFetch: BackendFetch, graph: BackendGraph) {

  return {

    /**
     * Get a specific order. Will return a reactive cached version if available, that will be rehydrated against backend
     * @param orderId 
     */
    get,

    /**
     * Refresh the order from server
     */
    refresh,

    /**
     * Gets a debug dump for the specified order
     */
    getDebugDump,
  };

  async function get(orderId: string) {
    const existing = cache.get(orderId);
      if (existing) {
        refresh(orderId);
        return existing;
      }

      await refresh(orderId);

      return cache.get(orderId);
  }

  async function getDebugDump(orderId: string) {
    const response = await portalFetch(`/core/resources/actors/order/${orderId}`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json'
      },
      body: JSON.stringify([{
        type: 'debugDump'
      }])
    })

    return response.json();
  }

  async function refresh(orderId: string) {

    const result = await graph.queryGraph<Models.Graph.Order>({
      edge: 'orders',
      limit: 1,
      filter: `orderId == ${orderId}`,
      node: {
        fields: [
          "orderId",
          "orderNumber",
          "channelKey",
          "currencyCode",
          "customerNumber",
          "invoiceRecipient",
          "locale",
          "notes",
          "orderState",
          "orderTaxTotal",
          "orderTotal",
          "taxIncluded",
          "tags",
          "dynamic",
          "createdAt"
        ],
        navigate: [{
          edge: 'orderDiscounts',
          node: {
            fields: [
              'discountId',
              'discountType',
              'createdAt',
              'calculatedTotal',
              'value',
              'description',
              "dynamic->'category'",
              "dynamic->'name'",
            ]
          }
        },{
          edge: "deliveries",
          node: {
            fields: [
              "deliveryNumber",
              "deliveryState",
              "deliveryAddress.addressLine1",
              "createdAt",
              "inventoryDate",
              "inventoryKey",
              "shippingPrice",
              "shippingTaxFactor",
              "shippingTaxTotal",
              "shippingTotal",
              "tags",
            ],
            navigate: [{
              edge: "orderLines",
              node: {
                fields: [
                  "orderLineId",
                  "orderLineNumber",
                  "productNumber",
                  "description",
                  "image",
                  "quantity",
                  "unitPrice",
                  "taxFactor",
                  "taxTotal",
                  "orderLineTotal",
                ]
              }
            }]
          }
        },{
          edge: "invoices",
          node: {
            fields: [
              "invoiceId",
              "invoiceNumber",
              "invoiceTotal",
            ]
          }
        },{
          edge: "journalEntries",
          node: {
            fields: [
              "amount",
              "orderJournalEntryId",
              "orderJournalEntryType",
              "timestamp",
            ]
          }
        },{
          edge: "payments",
          node: {
            fields: [
              "paymentId",
              "paymentNumber",
              "authorizedAmount",
            ],
            navigate: [{
              edge: "journalEntries",
              node: {
                fields: [
                  "amount",
                  "captureId",
                  "invoiceId",
                  "orderId",
                  "paymentJournalEntryId",
                  "paymentJournalEntryType",
                  "timestamp",
                ]
              }
            }]
          }
        },{
          edge: "activityLogs",
          node: {
            fields: [
              "activityLogId",
              "message",
            ]
          }
        }]
      }
    });

    if (result.length === 1)
    {
      cache.addOrUpdate(result[0]);
    }
  }

}
